import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Tooltip
} from '@mui/material';
import axios from 'axios';
import { formatDateWithTimezone } from '../../utils/timezoneUtils';
import { getWorkOrderDisplayNumber } from '../../utils/workOrderDisplayNumber';
import {
  getStatusColor,
  getPriorityColor,
  getUrgencyColor,
  isAmpAssemblyNotApplicable
} from './utils/maintenanceUtils';

const TYPE_LABELS = {
  INSPECTION: 'Inspection',
  MAINTENANCE: 'Maintenance',
  OVERHAUL: 'TSO (Time Since Overhaul)',
  REPAIR: 'Defect',
  REPLACEMENT: 'Replacement',
  TSN: 'TSN (Time Since New)',
  TSI: 'TSI (Time Since Inspection)',
  TSP: 'TSP (Time Since Part)',
  SLL: 'SLL (Service Life Limit)',
  OTL: 'OTL (Operational Time Limit)'
};

export const UPCOMING_SOURCE_LABELS = {
  WORK_ORDER: 'Work Order',
  COMPONENT_INSPECTION: 'Inspection',
  COMPONENT_SERVICE: 'Service',
  ASSEMBLY_TIME_SINCE: 'Assembly TSR',
  COMPONENT_TIME_SINCE: 'Component TSR',
  AMP_SCHEDULE: 'AMP Schedule',
  ADSB_SERVICE_BULLETIN: 'AD/SB'
};

const getTypeLabel = (type) => TYPE_LABELS[type] || type || '—';
export const getSourceLabel = (source) => UPCOMING_SOURCE_LABELS[source] || source || '—';

/** Display label for an intimation's source (upcoming type or plain text). */
export function getIntimationSourceDisplay(intimation) {
  if (!intimation) return '—';
  if (intimation.sourceType) {
    const label = getSourceLabel(intimation.sourceType);
    return intimation.sourcePlainText ? `${label} — ${intimation.sourcePlainText}` : label;
  }
  if ((intimation.sourcePlainText || '').trim()) return intimation.sourcePlainText.trim();
  return '—';
}

const getComponentNameDisplay = (item) =>
  (item?.componentName || item?.componentDescription || '').trim();

function formatRemainingHoursDisplay(decimalHours) {
  if (decimalHours == null || !Number.isFinite(Number(decimalHours))) return null;
  const n = Number(decimalHours);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const h = Math.floor(abs);
  let m = Math.round((abs - h) * 60);
  if (m >= 60) m = 59;
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

function formatRemainingUnitCell(unitKey, value) {
  if (value == null) return null;
  if (unitKey === 'hours') return formatRemainingHoursDisplay(value);
  if (unitKey === 'days') return value < 0 ? `Overdue ${Math.abs(value)}d` : `${value} days`;
  if (unitKey === 'cycles') return `${value} cycles`;
  if (unitKey === 'landings') return `${value} landings`;
  return String(value);
}

function getRemainingByUnitValue(item, unitKey) {
  const rbu = item?.remainingByUnit;
  if (!rbu || typeof rbu !== 'object') return null;
  const v = rbu[unitKey];
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

function StackedMetricLines({ lines, displayTimezone, mode = 'default' }) {
  if (!lines || lines.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
      {lines.map((line, i) => {
        let displayText = line.text;
        if (mode === 'due') {
          if (line.nextDueDateLow && line.nextDueDateHigh) {
            const low = displayTimezone ? formatDateWithTimezone(line.nextDueDateLow, displayTimezone) : line.nextDueDateLow;
            const high = displayTimezone ? formatDateWithTimezone(line.nextDueDateHigh, displayTimezone) : line.nextDueDateHigh;
            displayText = `Between ${low} - ${high}`;
          } else if (line.nextDueDate) {
            displayText = displayTimezone ? formatDateWithTimezone(line.nextDueDate, displayTimezone) : line.nextDueDate;
          }
        }

        const content = (
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.4 }}>
            {displayText}
          </Typography>
        );

        if (line.tooltip) {
          return (
            <Tooltip key={i} title={line.tooltip} arrow>
              <Box component="span" sx={{ display: 'block', cursor: 'help' }}>
                {content}
              </Box>
            </Tooltip>
          );
        }
        return <Box key={i}>{content}</Box>;
      })}
    </Box>
  );
}

function SummaryField({ label, children }) {
  if (children == null || children === false) return null;
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Grid>
  );
}

function ComponentAssemblyContent({ item }) {
  if (item.source === 'AMP_SCHEDULE') {
    if (isAmpAssemblyNotApplicable(item)) {
      return <Typography variant="body2">NA</Typography>;
    }
    if (item.ampAssemblyLabel) {
      return <Typography variant="body2">{item.ampAssemblyLabel}</Typography>;
    }
    if (item.assemblyType) {
      return (
        <>
          <Typography variant="body2">{item.assemblyType}</Typography>
          {item.assemblySerialNumber && (
            <Typography variant="caption" color="text.secondary" display="block">
              S/N: {item.assemblySerialNumber}
            </Typography>
          )}
        </>
      );
    }
    return <Typography variant="body2" color="text.secondary">None</Typography>;
  }

  if (item.componentCode) {
    return (
      <>
        <Typography variant="body2">{item.componentCode}</Typography>
        {item.componentPartNumber && (
          <Typography variant="caption" color="text.secondary" display="block">
            P/N: {item.componentPartNumber}
          </Typography>
        )}
        {item.componentPosition && (
          <Typography variant="caption" color="text.secondary" display="block">
            Pos: {item.componentPosition}
          </Typography>
        )}
        {item.assemblyType && (
          <Typography variant="caption" color="text.secondary" display="block">
            Assembly: {item.assemblyType}{item.assemblySerialNumber ? ` (${item.assemblySerialNumber})` : ''}
          </Typography>
        )}
      </>
    );
  }

  if (item.assemblyType) {
    return (
      <>
        <Typography variant="body2">{item.assemblyType}</Typography>
        {item.assemblySerialNumber && (
          <Typography variant="caption" color="text.secondary" display="block">
            S/N: {item.assemblySerialNumber}
          </Typography>
        )}
      </>
    );
  }

  return <Typography variant="body2" color="text.secondary">—</Typography>;
}

function DueAtContent({ item, displayTimezone }) {
  const displayDueDate = item.effectiveDueDate || item.dueDate;
  const dueAtLines =
    item.source === 'AMP_SCHEDULE'
      ? item.ampDueAtLines
      : item.source === 'ADSB_SERVICE_BULLETIN'
        ? item.adsbDueAtLines
        : null;

  if (Array.isArray(dueAtLines)) {
    return <StackedMetricLines lines={dueAtLines} displayTimezone={displayTimezone} mode="due" />;
  }
  if (displayDueDate) {
    return (
      <Typography variant="body2" sx={item.isExtended ? { fontWeight: 'bold' } : undefined}>
        {displayTimezone ? formatDateWithTimezone(displayDueDate, displayTimezone) : new Date(displayDueDate).toLocaleDateString()}
      </Typography>
    );
  }
  if (item.dueAtLimit) {
    return (
      <Typography variant="body2" color="text.secondary">
        {item.dueAtLimit.value} {item.dueAtLimit.unit}
      </Typography>
    );
  }
  if (item.dueByRemaining) {
    return (
      <Typography variant="body2" color="text.secondary">
        Due by remaining: {item.dueByRemaining.value} {item.dueByRemaining.unit}
      </Typography>
    );
  }
  return <Typography variant="body2" color="text.secondary">Not set</Typography>;
}

function RemainingContent({ item }) {
  const units = [
    { key: 'days', label: 'Days', colorFn: getUrgencyColor },
    { key: 'hours', label: 'Hours' },
    { key: 'cycles', label: 'Cycles' },
    { key: 'landings', label: 'Landings' }
  ];
  const chips = units
    .map(({ key, label, colorFn }) => {
      const value = getRemainingByUnitValue(item, key);
      const text = formatRemainingUnitCell(key, value);
      if (!text) return null;
      let color = 'default';
      if (colorFn) color = colorFn(value);
      else if (value < 0) color = 'error';
      return { key, label, text, color };
    })
    .filter(Boolean);

  if (chips.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {chips.map(({ key, label, text, color }) => (
        <Chip key={key} label={`${label}: ${text}`} size="small" color={color} variant="outlined" />
      ))}
    </Box>
  );
}

/**
 * Read-only summary of an Upcoming Maintenance row (same fields as the Upcoming tab table).
 */
const UpcomingMaintenanceItemSummary = ({ item, displayTimezone, title = 'Upcoming maintenance item' }) => {
  if (!item) return null;

  const componentName = getComponentNameDisplay(item);
  const woNumber = getWorkOrderDisplayNumber(item);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        <SummaryField label="Source">
          <Chip label={getSourceLabel(item.source)} size="small" color="primary" variant="outlined" />
        </SummaryField>
        <SummaryField label="Type">
          <Chip label={getTypeLabel(item.type)} size="small" />
        </SummaryField>
        <SummaryField label="Title">
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {item.title || '—'}
          </Typography>
          {item.description && item.description !== item.title && (
            <Typography variant="caption" color="text.secondary" display="block">
              {item.description}
            </Typography>
          )}
          {woNumber && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
              WO #{woNumber}
            </Typography>
          )}
        </SummaryField>
        <SummaryField label="Aircraft">
          <Typography variant="body2">{item.aircraftRegistration || '—'}</Typography>
          {item.aircraftModel && (
            <Typography variant="caption" color="text.secondary" display="block">
              {item.aircraftModel}
            </Typography>
          )}
        </SummaryField>
        {componentName && (
          <SummaryField label="Component name">
            <Typography variant="body2">{componentName}</Typography>
          </SummaryField>
        )}
        <SummaryField label="Component / assembly">
          <ComponentAssemblyContent item={item} />
          {item.ataChapter && (
            <Typography variant="caption" color="text.secondary" display="block">
              ATA: {item.ataChapter}{item.ataChapterName ? ` (${item.ataChapterName})` : ''}
            </Typography>
          )}
        </SummaryField>
        <SummaryField label="Last performed">
          <Typography variant="body2">{item.lastPerformed || '—'}</Typography>
        </SummaryField>
        <SummaryField label="Frequency">
          <Typography variant="body2">{item.frequency || '—'}</Typography>
        </SummaryField>
        <SummaryField label="Due at">
          <DueAtContent item={item} displayTimezone={displayTimezone} />
        </SummaryField>
        <SummaryField label="Remaining">
          <RemainingContent item={item} />
        </SummaryField>
        <SummaryField label="Priority">
          {item.priority ? (
            <Chip label={item.priority} size="small" color={getPriorityColor(item.priority)} />
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </SummaryField>
        <SummaryField label="Status">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {item.status ? (
              <Chip label={item.status} size="small" color={getStatusColor(item.status)} />
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
            {item.isExtended && (
              <Chip label="Extended" size="small" color="info" variant="outlined" />
            )}
          </Box>
        </SummaryField>
      </Grid>
    </Box>
  );
};

export default UpcomingMaintenanceItemSummary;

export function hasUpcomingSource(intimation) {
  return !!(intimation?.sourceType && intimation.sourceId != null);
}

export function hasAnySource(intimation) {
  return hasUpcomingSource(intimation) || !!(intimation?.sourcePlainText || '').trim() || !!intimation?.sourceType;
}

/** Compact stored-source panel when the live Upcoming row is unavailable. */
export function IntimationStoredSourceSummary({ intimation }) {
  if (!intimation || !hasAnySource(intimation)) return null;
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        Source
      </Typography>
      <Grid container spacing={1.5}>
        {intimation.sourceType && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Source type</Typography>
            <Chip label={getSourceLabel(intimation.sourceType)} size="small" color="primary" variant="outlined" />
          </Grid>
        )}
        {intimation.sourceId != null && intimation.sourceId !== '' && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Source ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{String(intimation.sourceId)}</Typography>
          </Grid>
        )}
        {intimation.sourceMetadata?.tsrIndex != null && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">TSR index</Typography>
            <Typography variant="body2">{String(intimation.sourceMetadata.tsrIndex)}</Typography>
          </Grid>
        )}
        {(intimation.sourcePlainText || '').trim() && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Source (plain text)</Typography>
            <Typography variant="body2">{intimation.sourcePlainText.trim()}</Typography>
          </Grid>
        )}
        {!intimation.sourceType && !(intimation.sourcePlainText || '').trim() && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">—</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

const UPCOMING_FETCH_PARAMS = {
  pastDays: 90,
  useDays: '1',
  days: 365,
  useHours: '1',
  hours: 500,
  useCycles: '1',
  cycles: 5000,
  useLandings: '1',
  landings: 2500
};

function buildUpcomingItemsMap(intimations = [], upcomingItems = []) {
  const map = {};
  (intimations || []).forEach((intimation) => {
    if (!hasUpcomingSource(intimation)) return;
    const item = findUpcomingItemForIntimation(intimation, upcomingItems);
    if (item) map[String(intimation._id)] = item;
  });
  return map;
}

/** Batch-resolve upcoming rows for intimations linked to Upcoming Maintenance. */
export function useUpcomingItemsMap(intimations = [], cachedUpcoming = [], enabled = true) {
  const [itemsMap, setItemsMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setItemsMap({});
      setLoading(false);
      return undefined;
    }

    const linked = (intimations || []).filter(hasUpcomingSource);
    if (linked.length === 0) {
      setItemsMap({});
      setLoading(false);
      return undefined;
    }

    const cachedMap = buildUpcomingItemsMap(linked, cachedUpcoming);
    const allCached = linked.every((intimation) => cachedMap[String(intimation._id)]);
    if (allCached) {
      setItemsMap(cachedMap);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    axios
      .get('/api/maintenance/upcoming', { params: UPCOMING_FETCH_PARAMS })
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.items || [];
        setItemsMap(buildUpcomingItemsMap(linked, [...cachedUpcoming, ...items]));
      })
      .catch(() => {
        if (!cancelled) setItemsMap(cachedMap);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [intimations, cachedUpcoming, enabled]);

  return { itemsMap, loading };
}

export function findUpcomingItemForIntimation(intimation, upcomingItems = []) {
  if (!intimation?.sourceType || intimation.sourceId == null) return null;
  const tsrIndex = intimation.sourceMetadata?.tsrIndex;
  return (upcomingItems || []).find((item) =>
    item.source === intimation.sourceType &&
    String(item.sourceId) === String(intimation.sourceId) &&
    (tsrIndex == null ? item.tsrIndex == null : item.tsrIndex === tsrIndex)
  ) || null;
}

/** Resolve upcoming row for a linked intimation (cache first, then API). */
export function useResolvedUpcomingItem(intimation, upcomingItems = [], enabled = true) {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !intimation?.sourceType || intimation.sourceId == null) {
      setResolved(null);
      setLoading(false);
      return undefined;
    }

    const cached = findUpcomingItemForIntimation(intimation, upcomingItems);
    if (cached) {
      setResolved(cached);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    axios
      .get('/api/maintenance/upcoming', {
        params: UPCOMING_FETCH_PARAMS
      })
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.items || [];
        setResolved(findUpcomingItemForIntimation(intimation, items));
      })
      .catch(() => {
        if (!cancelled) setResolved(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, intimation, upcomingItems]);

  return { item: resolved, loading };
}
